import { Divider, Tab, Tabs } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TAB } from '~/constants/Tab';

import { Select } from '@vestigas/ui/molecules';
import { ClientPortalTooltip } from '~/components/salesPackages/clientPortal/ClientPortalTooltip';
import { PackageBasicRestrictionTooltip } from '~/components/salesPackages/packageBasicRestriction/PackageBasicRestrictionTooltip';
import { DateRangeSelect } from '~/ui/molecules/DateInput';

import {
  DashboardCharts,
  DashboardFilterGroups,
  DashboardReport,
} from './components';
import { useDashboard } from './useDashboard';
import { useDashboardUnits } from './utils';

export function Dashboard() {
  const { t } = useTranslation('common');

  const selectableUnits = useDashboardUnits() ?? [];

  const {
    aggregationInterval,
    featureFlags,
    handleChangeTab,
    handleDateRangeChange,
    handleFilterChange,
    handlePredefinedDateRangeChange,
    handleUnitChange,
    individualDateRange,
    searchBody,
    selectedDateRange,
    selectedPredefinedDateRange,
    selectedTab,
    selectedUnit,
  } = useDashboard();

  const isChartsTab = selectedTab === TAB.DASHBOARD.CHARTS.INDEX;

  // Filter out "Alle Einheiten" option when on charts tab
  const filteredSelectableUnits = useMemo(() => {
    return isChartsTab
      ? selectableUnits.map((group) => ({
          ...group,
          options: group.options.filter((option) => Boolean(option.value)),
        }))
      : selectableUnits;
  }, [isChartsTab, selectableUnits]);

  return (
    <div className="main-padding flex h-full flex-col gap-4">
      <div className="rounded-md bg-white p-4 shadow-lg">
        <h3 className="text-lg font-medium">Filter auswählen</h3>
        <DashboardFilterGroups
          onChangeValue={(id, customField, filterValue) => {
            handleFilterChange(id, customField, filterValue);
          }}
        />
        <Divider sx={{ my: 2 }} />
        <div>
          <h3 className="pb-1 text-lg font-medium">Einheit</h3>
          <Select
            className="w-52"
            isClearable={!isChartsTab}
            options={filteredSelectableUnits}
            placeholder={t(($) => $.dashboard.unitSelect.placeholder)}
            value={selectedUnit ?? ''}
            onChange={handleUnitChange}
          />
        </div>
        <Divider sx={{ my: 2 }} />
        <div>
          <h3 className="pb-1 text-lg font-medium">Zeitraum</h3>
          <PackageBasicRestrictionTooltip>
            <ClientPortalTooltip>
              <DateRangeSelect
                className="min-w-52"
                dateRange={selectedDateRange}
                isDisabled={
                  featureFlags.clientPortal ||
                  featureFlags.packageBasicRestriction
                }
                isIndividualDateRange={individualDateRange}
                predefinedDateRange={selectedPredefinedDateRange}
                onDateRangeChange={handleDateRangeChange}
                onPredefinedDateRangeChange={handlePredefinedDateRangeChange}
              />
            </ClientPortalTooltip>
          </PackageBasicRestrictionTooltip>
        </div>
      </div>
      <div className="flex flex-1 flex-col rounded-md bg-white p-4 shadow-lg">
        <Tabs
          className="border-b border-gray-200"
          value={selectedTab}
          onChange={handleChangeTab}
        >
          <Tab
            data-testid={TAB.DASHBOARD.CHARTS.TESTID}
            label={TAB.DASHBOARD.CHARTS.NAME}
          />
          <Tab
            data-testid={TAB.DASHBOARD.REPORT.TESTID}
            disabled={
              featureFlags.clientPortal || featureFlags.packageBasicRestriction
            }
            label={TAB.DASHBOARD.REPORT.NAME}
          />
        </Tabs>
        {isChartsTab ? (
          <DashboardCharts
            aggregationInterval={aggregationInterval}
            searchBody={searchBody}
            selectedDateRange={selectedDateRange}
            selectedUnit={selectedUnit}
          />
        ) : null}
        {selectedTab === TAB.DASHBOARD.REPORT.INDEX && (
          <DashboardReport
            dateRange={selectedDateRange}
            predefinedDateRange={selectedPredefinedDateRange}
            searchBody={searchBody}
          />
        )}
      </div>
      <div
        className="min-h-8"
        /* This is a hacky workaround to get the padding bottom of 2rem. It is applied as child container to all divs with main-padding */
        /* A better solution would be to make the parent container min-h-fit so that the padding of main-padding is applied. */
        /* However, min-h-fit seems to not work with h-fill or generally with flexbox and flex-1. */
      />
    </div>
  );
}
